import React from 'react'
import Helmet from 'react-helmet'
import { useLanguage } from '../hooks'
import { getAlternatePaths } from '../translations/translatePath'

type Props = {
  title?: string
  pathname: string
  description?: string
  // Caminho canônico em PT (ex: '/jeito-dimas/'), usado para calcular as
  // 3 URLs alternativas do hreflang. Nas páginas em EN/ES, `pathname` já
  // vem traduzido pelo translatePath, então esse valor precisa ser
  // passado explicitamente com o path original em PT.
  basePath?: string
}

const siteUrl = 'https://www.dimasconstrucoes.com.br'

const path = (pathname: string) => {
  if (process.env.GATSBY_LANGUAGE === 'en') return '/en' + pathname
  else if (process.env.GATSBY_LANGUAGE === 'es') return '/es' + pathname
  else return pathname
}

const defaultDescription =
  'Nosso nome entrega o que é imutável. O que é nossa história. O Nosso DNA. Conheça o jeito Dimas de ser.'

const organizationJsonLd = (description: string) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://www.dimasconstrucoes.com.br/#organization',
      name: 'Dimas Construções',
      url: 'https://www.dimasconstrucoes.com.br/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.dimasconstrucoes.com.br/logo.jpg',
      },
      description,
      telephone: '+55 48 3381-3031',
      sameAs: [
        'https://www.facebook.com/empreendimentos/',
        'https://www.instagram.com/dimasconstrucoes/',
        'https://br.linkedin.com/showcase/dimas-empreendimentos/',
        'https://www.youtube.com/channel/UC8tSXpHu_HV9m0rdxwYffWg',
      ],
    },
    {
      '@type': 'LocalBusiness',
      '@id': 'https://www.dimasconstrucoes.com.br/#localbusiness',
      name: 'Dimas Construções',
      image: 'https://www.dimasconstrucoes.com.br/logo.jpg',
      url: 'https://www.dimasconstrucoes.com.br/',
      telephone: '+55 48 3381-3031',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Presidente Kennedy, 815',
        addressLocality: 'São José',
        addressRegion: 'SC',
        postalCode: '88101-001',
        addressCountry: 'BR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '-27.6013383',
        longitude: '-48.6158606',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: [
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
          ],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      parentOrganization: {
        '@id': 'https://www.dimasconstrucoes.com.br/#organization',
      },
    },
  ],
})

const Head = ({
  title,
  pathname,
  description = '',
  basePath,
}: Props) => {
  const { t } = useLanguage()

  const isHomePage = pathname === '/'
  const translatedTitle = isHomePage ? t('Head.title') : title
  const translatedDescription = isHomePage ? t('Head.defaultDescription') : description

  const alternatePaths = getAlternatePaths(basePath || pathname)

  return (
    <Helmet>
      <meta
        property="og:image"
        itemProp="image"
        content="https://www.dimasconstrucoes.com.br/opengraph.jpg"
      />
      <meta
        name="twitter:image"
        content="https://www.dimasconstrucoes.com.br/opengraph.jpg"
      />
      <title>{translatedTitle}</title>
      <meta property="og:title" content={translatedTitle} />
      <meta property="og:site_name" content="Dimas Construções" />
      <meta name="description" content={translatedDescription} />
      <meta
        property="og:description"
        content={translatedDescription}
      />
      <link
        rel="canonical"
        href={'https://www.dimasconstrucoes.com.br' + path(pathname)}
      />
      <meta
        property="og:url"
        content={'https://www.dimasconstrucoes.com.br' + path(pathname)}
      />
      <link
        rel="alternate"
        hrefLang="pt-BR"
        href={siteUrl + alternatePaths.pt}
      />
      <link
        rel="alternate"
        hrefLang="en"
        href={siteUrl + '/en' + alternatePaths.en}
      />
      <link
        rel="alternate"
        hrefLang="es"
        href={siteUrl + '/es' + alternatePaths.es}
      />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={siteUrl + alternatePaths.pt}
      />
      <link rel="shortcut icon" href="/favicon.ico" type="image/x-icon" />
      <meta property="og:locale" content="pt_BR" />
      <meta charSet="utf-8" />
      <meta name="keywords" content="Dimas Construções" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, shrink-to-fit=no"
      />

      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify(organizationJsonLd(translatedDescription))}
        </script>
      )}
    </Helmet>
  )
}

export default Head
